/**
 * Home Page - Topsail Software Inc.
 * Company information and services showcase
 */

import React from 'react';
import { Link } from 'react-router-dom';
import topsailLogo from '../assets/topsail-logo.jpg';
import BuildTag from '../components/BuildTag';

const services = [
    {
        icon: '🌐',
        title: 'Full-Stack Web Applications',
        description: 'Database-powered web applications built with modern technologies and best practices',
        color: '#2c5f7d'
    },
    {
        icon: '📱',
        title: 'Progressive Web Apps',
        description: 'Mobile-optimized PWAs that deliver native app experiences on any device',
        color: '#4a90a4'
    },
    {
        icon: '🗄️',
        title: 'Database Solutions',
        description: 'Robust database design, optimization, and management for your data needs',
        color: '#1a2332'
    },
    {
        icon: '🎨',
        title: 'Web Design',
        description: 'Beautiful, responsive websites that engage users and drive results',
        color: '#ffd700'
    },
    {
        icon: '🤖',
        title: 'Artificial Intelligence',
        description: 'AI-powered solutions including chatbots, automation, and intelligent systems',
        color: '#2c5f7d'
    },
    {
        icon: '⚙️',
        title: 'Custom Development',
        description: 'Tailored software solutions designed specifically for your unique requirements',
        color: '#4a90a4'
    }
];

function HomePage() {
    return (
        <div className="home-page">
            {/* Navbar */}
            <nav className="navbar navbar-dark bg-lighthouse-gradient">
                <div className="container">
                    <Link to="/" className="navbar-brand mb-0 h1">
                        ⚓ Lighthouse
                    </Link>
                    <div className="d-flex gap-3">
                        <Link to="/home" className="nav-link text-white">Home</Link>
                        <Link to="/subscription" className="nav-link text-white">Pricing</Link>
                        <Link to="/" className="nav-link text-white-50">Demos</Link>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="hero bg-lighthouse-gradient text-white py-5">
                <div className="container text-center py-5">
                    <div className="mb-4">
                        <img
                            src={topsailLogo}
                            alt="Topsail Software Inc."
                            style={{
                                maxWidth: '300px',
                                height: 'auto',
                                borderRadius: '1rem',
                                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)'
                            }}
                        />
                    </div>
                    <h1 className="display-3 fw-bold mb-4">
                        Topsail Software Inc.
                    </h1>
                    <p className="lead mb-4">
                        A web-centric full-stack software development shop in Nova Scotia, serving the world
                    </p>
                    <p className="text-white-50 fs-5">
                        We specialize in crafting exceptional digital experiences that drive business success
                    </p>
                </div>
            </section>

            {/* Services Section */}
            <section className="services py-5">
                <div className="container">
                    <div className="text-center mb-5">
                        <h2 className="fw-bold mb-3">Our Expertise</h2>
                        <p className="text-muted">
                            Comprehensive software development services tailored to your needs
                        </p>
                    </div>

                    <div className="row g-4">
                        {services.map((service, index) => (
                            <div key={index} className="col-md-6 col-lg-4">
                                <div className="card card-hover h-100 border-0 shadow">
                                    <div className="card-body p-4">
                                        <div
                                            className="display-4 mb-3"
                                            style={{ fontSize: '3rem' }}
                                        >
                                            {service.icon}
                                        </div>
                                        <h4 className="fw-bold mb-3">{service.title}</h4>
                                        <p className="text-muted mb-0">
                                            {service.description}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* About Section */}
            <section className="about bg-lighthouse-light py-5">
                <div className="container">
                    <div className="row align-items-center">
                        <div className="col-lg-6 mb-4 mb-lg-0">
                            <h2 className="fw-bold mb-4">Why Choose Topsail?</h2>
                            <p className="text-muted mb-3">
                                Based in beautiful Nova Scotia, we bring world-class software development
                                expertise to clients around the globe. Our team combines technical excellence
                                with a deep understanding of business needs.
                            </p>
                            <p className="text-muted mb-4">
                                From concept to deployment, we partner with you to create solutions that
                                are scalable, maintainable, and built to last.
                            </p>
                            <div className="d-flex gap-3">
                                <Link to="/subscription" className="btn btn-primary btn-lg">
                                    View Our Products
                                </Link>
                                <Link to="/" className="btn btn-outline-primary btn-lg">
                                    Try a Demo
                                </Link>
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div className="card border-0 shadow-lg">
                                <div className="card-body p-5 text-center">
                                    <div className="display-1 mb-3">⚓</div>
                                    <h3 className="fw-bold mb-3">Featured Product</h3>
                                    <h4 className="text-primary mb-3">Lighthouse</h4>
                                    <p className="text-muted mb-4">
                                        AI-powered customer assistance platform that guides your
                                        customers home, 24/7
                                    </p>
                                    <Link to="/subscription" className="btn btn-primary">
                                        Learn More →
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="cta bg-lighthouse-gradient text-white py-5">
                <div className="container text-center py-4">
                    <h2 className="fw-bold mb-3">Ready to Build Something Amazing?</h2>
                    <p className="lead mb-4">
                        Let's discuss how we can help bring your vision to life
                    </p>
                    <div className="d-flex gap-3 justify-content-center">
                        <a href="mailto:contact@topsailsoftware.com" className="btn btn-light btn-lg">
                            Get in Touch
                        </a>
                        <Link to="/subscription" className="btn btn-outline-light btn-lg">
                            View Pricing
                        </Link>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-dark text-white-50 py-4">
                <div className="container text-center">
                    <p className="mb-2">
                        <strong className="text-white">Topsail Software Inc.</strong>
                    </p>
                    <p className="mb-1">
                        Nova Scotia, Canada • Serving the World
                    </p>
                    <BuildTag />
                </div>
            </footer>
        </div>
    );
}

export default HomePage;
